<script lang="ts" setup>
import { ref, computed, onMounted, type PropType } from 'vue'
import UpgradePremium from '~/pages/upgrade-premium.vue'
const { hasPremiumAccess } = useAuth();

interface GraphDataPoint {
  timestamp: string | Date
  pressSnsrExtractdBar: number
  pressSnsrrRetractdBar: number
  pullForceKg: number
  tonsSetPoint: number
}

interface BoltGraphItem {
  pegNumber: number
  vault: number
  section: number
  description: string
  startTime: string | Date
  statusId: number
  status: string
  data: GraphDataPoint[]
}

const props = defineProps({
  boltGraphData: {
    type: Array as PropType<BoltGraphItem[]>,
    required: true,
    default: () => []
  }
})

const selectedIndex = ref(0)
const selectedBolt = computed(() => {
  if (!props.boltGraphData || props.boltGraphData.length === 0) return null
  return props.boltGraphData[selectedIndex.value]
})

const formatDate = (d: string | Date) => {
  if (!d) return '-'
  return new Date(d).toLocaleString(undefined, {
    year: 'numeric',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

const getStatusColor = (status: string) => {
  const s = status.toString().toLowerCase();
  if (s.includes('complete')) return 'success';
  if (s.includes('waiting')) return 'warning';
  if (s.includes('failed')) return 'error';
  return 'default';
};

const pegScale = 60;
const vaultScale = 50;
const paddingX = 60;
const paddingY = 40;

const minPeg = computed(() => {
  if (props.boltGraphData.length === 0) return 0;
  return Math.min(...props.boltGraphData.map(b => b.pegNumber - 1));
});

const maxPeg = computed(() => {
  if (props.boltGraphData.length === 0) return 10;
  return Math.max(10, ...props.boltGraphData.map(b => b.pegNumber + 1));
});

const minVault = computed(() => {
  if (props.boltGraphData.length === 0) return -3;
  return Math.min(-3, ...props.boltGraphData.map(b => b.vault));
});

const maxVault = computed(() => {
  if (props.boltGraphData.length === 0) return 3;
  return Math.max(3, ...props.boltGraphData.map(b => b.vault));
});

const svgWidth = computed(() => {
  return (maxPeg.value - minPeg.value) * pegScale + paddingX * 2;
});

const svgHeight = computed(() => {
  const range = maxVault.value - minVault.value; 
  return range * vaultScale + paddingY * 2;
});

const getX = (peg: number, section: number = 0) => {
  return paddingX + (peg + section - minPeg.value) * pegScale;
};

const getY = (vault: number) => {
  return paddingY + (vault - minVault.value) * vaultScale;
};

const pegTicks = computed(() => {
  const ticks = [];
  for (let i = Math.floor(minPeg.value); i <= Math.ceil(maxPeg.value); i++) {
    ticks.push(i);
  }
  return ticks;
});

const vaultTicks = computed(() => {
  const ticks = [];
  for (let i = Math.floor(minVault.value); i <= Math.ceil(maxVault.value); i++) {
    ticks.push(i);
  }
  return ticks;
});

const sections = [0.0, 0.3, 0.6];

const scrollContainer = ref<HTMLElement | null>(null);
const scrollLeft = ref(0);

const onScroll = (e: Event) => {
  const target = e.target as HTMLElement;
  scrollLeft.value = target.scrollLeft;
};

onMounted(() => {
  if (scrollContainer.value) {
    scrollLeft.value = scrollContainer.value.scrollLeft;
  }
});
</script>

<template>
  <UpgradePremium v-if="!hasPremiumAccess" style="min-height: 400px;" />
  <v-card v-else class="d-flex flex-column" height="950" elevation="2">
    <!-- Legend -->
    <div class="d-flex flex-wrap gap-4 pa-4 border-b bg-grey-lighten-5">
      <div class="d-flex align-center mr-6">
        <v-badge dot color="success" inline class="mr-2"></v-badge>
        <span class="text-caption font-weight-bold text-medium-emphasis text-uppercase">Complete</span>
      </div>
      <div class="d-flex align-center mr-6">
        <v-badge dot color="warning" inline class="mr-2"></v-badge>
        <span class="text-caption font-weight-bold text-medium-emphasis text-uppercase">Waiting</span>
      </div>
      <div class="d-flex align-center">
        <v-badge dot color="error" inline class="mr-2"></v-badge>
        <span class="text-caption font-weight-bold text-medium-emphasis text-uppercase">Failed</span>
      </div>
    </div>

    <!-- Chart Scroll Container -->
    <div class="flex-grow-1 overflow-auto position-relative bg-grey-lighten-5" ref="scrollContainer" @scroll="onScroll">
      <svg :width="svgWidth" :height="svgHeight" class="d-block">
        <g class="grid">
          <line v-for="v in vaultTicks" :key="'vline-'+v"
            :x1="paddingX - 10" :y1="getY(v)"
            :x2="svgWidth - paddingX" :y2="getY(v)"
            stroke="#e2e8f0" stroke-width="1" stroke-dasharray="4"
          />
          <g v-for="p in pegTicks" :key="'pgroup-'+p">
            <line v-for="s in sections" :key="'sline-'+p+'-'+s"
              :x1="getX(p, s)" :y1="paddingY"
              :x2="getX(p, s)" :y2="svgHeight - paddingY"
              stroke="#e2e8f0" stroke-width="1"
              :stroke-dasharray="s === 0 ? undefined : '4'"
            />
          </g>
          
          <line 
            :x1="paddingX - 10" :y1="getY(0)"
            :x2="svgWidth - paddingX" :y2="getY(0)"
            stroke="#94a3b8" stroke-width="2"
            opacity="0.5"
          />
        </g>

        <g class="x-axis-labels">
          <text v-for="p in pegTicks" :key="'plabel-'+p"
            :x="getX(p)" :y="svgHeight - paddingY + 20"
            text-anchor="middle"
            class="axis-label"
          >
            {{ p }}
          </text>
          <text :x="svgWidth/2" :y="svgHeight" text-anchor="middle" class="axis-title" fill="#64748b">
            Peg Number (Distance)
          </text>
        </g>

        <g class="data-points">
          <circle v-for="(bolt, idx) in boltGraphData" :key="idx"
            :cx="getX(bolt.pegNumber, bolt.section)"
            :cy="getY(bolt.vault)"
            r="6"
            :class="['bolt-dot', `text-${getStatusColor(bolt.status)}`, { 'selected': selectedIndex === idx }]"
            fill="currentColor"
            @click="selectedIndex = idx"
          >
            <title>{{ bolt.description }} (Peg: {{ bolt.pegNumber }}, Vault: {{ bolt.vault }}, Section: {{ bolt.section }})</title>
          </circle>
        </g>

        <g class="y-axis-sticky">
          <rect :x="scrollLeft" y="0" :width="paddingX" :height="svgHeight" fill="#FAFAFA" />
          <text v-for="v in vaultTicks" :key="'vlabel-'+v"
            :x="scrollLeft + paddingX - 15" :y="getY(v)"
            text-anchor="end" alignment-baseline="middle"
            class="axis-label"
          >
            {{ v }}
          </text>
          <text :x="scrollLeft + 15" :y="svgHeight/2" :transform="`rotate(-90, ${scrollLeft + 15}, ${svgHeight/2})`" text-anchor="middle" class="axis-title" fill="#64748b">
            Right+ (Vault) Left -
          </text>
        </g>
      </svg>
    </div>

    <!-- Info Panel -->
    <v-card-text class="bg-white border-t pa-0" style="flex: 0 0 auto;">
      <div v-if="selectedBolt" class="pa-4">
        <div class="d-flex justify-space-between align-center mb-4">
          <h4 class="text-h6 font-weight-bold text-blue-grey-darken-3">{{ selectedBolt.description }}</h4>
          <v-chip :color="getStatusColor(selectedBolt.status)" size="small" class="font-weight-bold text-uppercase" variant="tonal">
            {{ selectedBolt.status }}
          </v-chip>
        </div>
        <v-row dense>
          <v-col cols="6" sm="3">
            <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Peg Number</div>
            <div class="text-body-1 font-weight-bold font-monospace">{{ selectedBolt.pegNumber }}</div>
          </v-col>
          <v-col cols="6" sm="3">
            <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Vault</div>
            <div class="text-body-1 font-weight-bold font-monospace">{{ selectedBolt.vault }}</div>
          </v-col>
          <v-col cols="6" sm="3">
            <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Section</div>
            <div class="text-body-1 font-weight-bold font-monospace">{{ selectedBolt.section }}</div>
          </v-col>
          <v-col cols="6" sm="3">
            <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Time</div>
            <div class="text-body-1 font-weight-bold">{{ formatDate(selectedBolt.startTime) }}</div>
          </v-col>
        </v-row>
      </div>
      <div v-else class="d-flex align-center justify-center pa-6 text-medium-emphasis font-italic">
        Select a bolt to view details
      </div>
    </v-card-text>
  </v-card>
</template>

<style scoped>
.axis-label {
  font-size: 0.75rem;
  fill: #64748b;
  font-family: 'Courier New', Courier, monospace !important;
}

.axis-title {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.bolt-dot {
  cursor: pointer;
  transition: r 0.2s, stroke-width 0.2s;
  stroke: white;
  stroke-width: 2px;
}

.bolt-dot:hover {
  r: 8;
}

.bolt-dot.selected {
  r: 9;
  stroke: #0f172a;
  stroke-width: 3px;
}
.font-monospace {
  font-family: 'Courier New', Courier, monospace !important;
}
</style>